// Scroll-driven bee swarm. As the visitor scrolls a section into view, a small
// swarm of honeybees drifts across the viewport and then removes itself. Purely
// decorative background ambiance: a fixed, pointer-events-none, dimmed overlay
// tucked low in the stack.
//
// Each bee gets a random DEPTH (0 = far, 1 = near) that drives scale, opacity,
// blur, speed and bob amplitude, so the swarm reads as a 3D space rather than a
// single flat plane. Paths curve (a mid-flight arc) and carry a net vertical
// drift, so bees travel in many directions, not just left↔right.
//
// Motion is CSS-driven (GPU transforms) so React only mounts/unmounts a handful
// of nodes. Production guards: concurrency cap, per-section cooldown, no burst
// on first paint, paused while the tab is hidden, and fully disabled under
// prefers-reduced-motion.

import React, { useCallback, useEffect, useRef, useState } from 'react';

// Sections that arm a fly-by when they scroll into view. The hero is omitted on
// purpose — it is already on screen at load and would fire immediately.
const TRIGGER_IDS = ['rf-prizes', 'rf-participants', 'rf-machine', 'rf-transparency'];

const MAX_CONCURRENT = 3; // hard ceiling on bees alive at once — keep it sparse
const SPAWN_COOLDOWN_MS = 6500; // per-section re-trigger guard
const ARM_DELAY_MS = 500; // ignore intersections during initial layout
// Trigger when a section crosses the middle band of the viewport. Using a
// center band (via rootMargin) rather than a coverage ratio fires reliably for
// sections both shorter and much taller than the screen.
const IO_ROOT_MARGIN = '-42% 0px -42% 0px';

// Off-screen X anchors (vw) just past each edge.
const EDGE_L = -18;
const EDGE_R = 116;

const rand = (min, max) => min + Math.random() * (max - min);
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Pick a flight path. Three modes give variety instead of edge-to-edge only:
//   pass   — cross the whole screen, fading out near the far edge.
//   vanish — enter from an edge and dissolve mid-screen ("disappear halfway").
//   drift  — materialise on-screen and dissolve again without touching an edge.
// Positions are in vw; the fade keyframe handles the graceful in/out, so every
// bee eases into view and eases away rather than popping at a hard boundary.
const pickPath = () => {
  const r = Math.random();
  const fromLeft = Math.random() < 0.5;
  if (r < 0.4) {
    return fromLeft ? { x0: EDGE_L, x1: EDGE_R } : { x0: EDGE_R, x1: EDGE_L };
  }
  if (r < 0.72) {
    return fromLeft ? { x0: EDGE_L, x1: rand(42, 66) } : { x0: EDGE_R, x1: rand(34, 58) };
  }
  const start = fromLeft ? rand(12, 40) : rand(60, 88);
  const span = rand(24, 46) * (fromLeft ? 1 : -1);
  return { x0: start, x1: clamp(start + span, 8, 92) };
};

// One bee's randomised parameters. `order` staggers a swarm so bees don't
// overlap perfectly. `depth` (0 far → 1 near) drives the sense of 3D.
const makeBee = (id, order) => {
  const depth = Math.random();
  const { x0, x1 } = pickPath();
  const dist = Math.abs(x1 - x0);
  const speed = 11 + depth * 11; // vw/s — near bees travel faster (parallax)
  const drift = rand(-22, 22); // net vertical travel, vh
  return {
    id,
    depth,
    x0,
    x1,
    xm: (x0 + x1) / 2, // horizontal position at the mid (arc) keyframe
    facingLeft: x1 < x0, // SVG is drawn heading right; flip when moving left
    band: rand(8, 82), // starting vertical position, vh from top
    scale: 0.42 + depth * 0.66, // 0.42 (far) → 1.08 (near)
    opacity: 0.18 + depth * 0.28, // 0.18 → 0.46 — faint, sits in the far bg
    blur: (1 - depth) * 2.1, // far bees soften (depth of field), px
    duration: clamp(dist / speed, 3.4, 13), // scaled to the actual distance, s
    delay: order * rand(0.3, 0.8), // swarm stagger
    drift,
    arc: drift * 0.5 + rand(-14, 14), // mid-flight vertical arc → curved path, vh
    bob: rand(0.8, 1.5), // wing-bob period, s
    bobAmp: 5 + depth * 8, // near bees bob more, px
    tilt: clamp(drift * 0.5 + rand(-4, 4), -16, 16), // bank toward travel, deg
  };
};

// A single honeybee. `bee.id` keeps SVG gradient/clip ids unique per instance.
const Bee = ({ bee, onDone }) => {
  const bodyId = `rf-bee-body-${bee.id}`;
  const wingId = `rf-bee-wing-${bee.id}`;
  const clipId = `rf-bee-clip-${bee.id}`;
  const doneRef = useRef(false);

  const finish = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    onDone(bee.id);
  }, [bee.id, onDone]);

  // Fallback in case the flight animationend never fires (e.g. tab was hidden).
  useEffect(() => {
    const ttl = (bee.duration + bee.delay) * 1000 + 800;
    const t = setTimeout(finish, ttl);
    return () => clearTimeout(t);
  }, [bee.duration, bee.delay, finish]);

  return (
    <div
      className={`rf-bee${bee.facingLeft ? ' rf-bee--flip' : ''}`}
      style={{
        top: `${bee.band}vh`,
        animationDuration: `${bee.duration}s`,
        animationDelay: `${bee.delay}s`,
        '--rf-bee-x0': `${bee.x0}vw`,
        '--rf-bee-xm': `${bee.xm}vw`,
        '--rf-bee-x1': `${bee.x1}vw`,
        '--rf-bee-arc': `${bee.arc}vh`,
        '--rf-bee-drift': `${bee.drift}vh`,
        '--rf-bee-opacity': bee.opacity,
      }}
      onAnimationEnd={(e) => {
        // Fires when the flight/fade end (both run this length); loops never end.
        if (e.target === e.currentTarget) finish();
      }}
    >
      <div className="rf-bee__flip">
        <div
          className="rf-bee__body"
          style={{
            '--rf-bee-scale': bee.scale,
            '--rf-bee-bob': `${bee.bob}s`,
            '--rf-bee-bob-amp': `${bee.bobAmp}px`,
            '--rf-bee-tilt': `${bee.tilt}deg`,
            '--rf-bee-blur': `${bee.blur}px`,
          }}
        >
          <svg viewBox="0 0 64 48" width="50" height="38" fill="none" aria-hidden="true">
            <defs>
              <radialGradient id={bodyId} cx="42%" cy="32%" r="78%">
                <stop offset="0%" stopColor="#ffd97e" />
                <stop offset="55%" stopColor="#f7b13d" />
                <stop offset="100%" stopColor="#df9020" />
              </radialGradient>
              <linearGradient id={wingId} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#ffffff" stopOpacity="0.92" />
                <stop offset="100%" stopColor="#cfe0f5" stopOpacity="0.6" />
              </linearGradient>
              <clipPath id={clipId}>
                <ellipse cx="26" cy="27" rx="17.5" ry="12" />
              </clipPath>
            </defs>

            {/* stinger */}
            <path d="M7 27 L1.5 25 L1.5 29 Z" fill="#5a4326" />

            {/* legs */}
            <g stroke="#4a3416" strokeWidth="1.3" strokeLinecap="round" opacity="0.85">
              <path d="M20 37 L17 43" />
              <path d="M26 38 L25 44" />
              <path d="M32 37 L34 43" />
            </g>

            {/* striped abdomen (shaded) */}
            <g clipPath={`url(#${clipId})`}>
              <ellipse cx="26" cy="27" rx="17.5" ry="12" fill={`url(#${bodyId})`} />
              <g fill="#432f15" opacity="0.82">
                <rect x="13.5" y="14" width="4.6" height="26" rx="2.3" />
                <rect x="21.5" y="14" width="4.6" height="26" rx="2.3" />
                <rect x="29.5" y="14" width="4.6" height="26" rx="2.3" />
              </g>
            </g>
            {/* rim light on the abdomen */}
            <ellipse
              cx="26"
              cy="27"
              rx="17.5"
              ry="12"
              fill="none"
              stroke="#fff2d0"
              strokeOpacity="0.4"
              strokeWidth="1"
            />

            {/* fuzzy thorax */}
            <circle cx="40" cy="25" r="7.6" fill="#eaa32f" />
            <circle
              cx="40"
              cy="25"
              r="7.2"
              fill="none"
              stroke="#7a4f16"
              strokeOpacity="0.35"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeDasharray="0.8 2.4"
            />

            {/* head */}
            <circle cx="49" cy="24" r="6.6" fill="#3a2a14" />
            <ellipse cx="51.6" cy="22.4" rx="2.1" ry="2.5" fill="#fff7e6" />
            <circle cx="52.1" cy="22.6" r="1.05" fill="#1c1917" />

            {/* antennae */}
            <g stroke="#3a2a14" strokeWidth="1.4" strokeLinecap="round" fill="none">
              <path d="M52 18 C55 13 58 12 60 13" />
              <path d="M49 16 C50 11 52 9 54 9" />
            </g>
            <circle cx="60" cy="13" r="1.6" fill="#3a2a14" />
            <circle cx="54" cy="9" r="1.6" fill="#3a2a14" />

            {/* wings — flutter group */}
            <g className="rf-bee__wings">
              <ellipse
                cx="24"
                cy="12"
                rx="12"
                ry="7"
                fill={`url(#${wingId})`}
                stroke="#a9c6e6"
                strokeOpacity="0.5"
                strokeWidth="0.7"
                transform="rotate(-22 24 12)"
              />
              <ellipse
                cx="31.5"
                cy="14.5"
                rx="8.6"
                ry="5.1"
                fill={`url(#${wingId})`}
                fillOpacity="0.82"
                stroke="#a9c6e6"
                strokeOpacity="0.45"
                strokeWidth="0.7"
                transform="rotate(-5 31.5 14.5)"
              />
              {/* wing veins */}
              <path
                d="M15 12 Q25 8 34 12"
                stroke="#bcd4ee"
                strokeOpacity="0.6"
                strokeWidth="0.5"
                fill="none"
              />
            </g>
          </svg>
        </div>
      </div>
    </div>
  );
};

const ScrollBees = ({ reducedMotion = false }) => {
  const [bees, setBees] = useState([]);
  const idRef = useRef(0);
  const armedRef = useRef(false);
  const lastSpawnRef = useRef(new Map()); // element -> timestamp
  const visibleRef = useRef(new Map()); // element -> boolean (prev visibility)

  const removeBee = useCallback((id) => {
    setBees((prev) => prev.filter((b) => b.id !== id));
  }, []);

  const spawnSwarm = useCallback((size) => {
    if (typeof document !== 'undefined' && document.hidden) return;
    setBees((prev) => {
      const room = MAX_CONCURRENT - prev.length;
      if (room <= 0) return prev;
      const count = Math.min(size, room);
      const next = [];
      for (let i = 0; i < count; i += 1) {
        idRef.current += 1;
        next.push(makeBee(idRef.current, i));
      }
      return [...prev, ...next];
    });
  }, []);

  useEffect(() => {
    if (reducedMotion) return undefined;
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return undefined;

    const targets = TRIGGER_IDS.map((id) => document.getElementById(id)).filter(Boolean);
    if (!targets.length) return undefined;

    const armTimer = setTimeout(() => {
      armedRef.current = true;
    }, ARM_DELAY_MS);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target;
          const nowVisible = entry.isIntersecting; // section is within the center band
          const wasVisible = visibleRef.current.get(el) || false;
          visibleRef.current.set(el, nowVisible);

          // Only fire on an off→on transition, once armed, respecting cooldown.
          if (!nowVisible || wasVisible || !armedRef.current) return;
          const now = Date.now();
          const last = lastSpawnRef.current.get(el) || 0;
          if (now - last < SPAWN_COOLDOWN_MS) return;
          lastSpawnRef.current.set(el, now);
          // Usually a lone bee; occasionally a pair. Keeps the page uncluttered.
          spawnSwarm(Math.random() < 0.25 ? 2 : 1);
        });
      },
      { threshold: 0, rootMargin: IO_ROOT_MARGIN }
    );

    targets.forEach((el) => observer.observe(el));

    // Clear any bees mid-flight when the tab is hidden so nothing "jumps" on
    // return; new ones spawn naturally on the next scroll.
    const onVisibility = () => {
      if (document.hidden) setBees([]);
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearTimeout(armTimer);
      observer.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [reducedMotion, spawnSwarm]);

  if (reducedMotion || !bees.length) return null;

  return (
    <div className="rf-bees" aria-hidden="true">
      {bees.map((bee) => (
        <Bee key={bee.id} bee={bee} onDone={removeBee} />
      ))}
    </div>
  );
};

export default ScrollBees;
